import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Controller, useForm } from "react-hook-form";
import { useLocation, Navigate } from "react-router-dom";
import { alpha, Divider, Slide } from "@mui/material";
import logo from "../../utils/images/ridePlus.png";
import { useContext, useEffect, useState } from "react";
import AuthContext from "../../context/AuthProvider";
import { useSnackbar } from "notistack";
import { useMutation, useQuery } from "react-query";
import axios from "../../api/axios";
import Consent from "../consent/consent";
import RegisterWithEmail from "../registration/registerWithEmail";
import ForgetPassword from "./forgetPassword";
import { queryClient } from "../../App";
import useTenantBranding from "../../hooks/useTenantBranding";
import {
  resolvePostAuthDestination,
  shouldShowConsentAfterAuth,
} from "../../utils/postAuthNavigation";
import AuthLayout from "../../components/auth/AuthLayout";
import AuthTextField from "../../components/auth/AuthTextField";
import PrimaryButton from "../../components/auth/PrimaryButton";

function SignInWithEmail({ consentId }) {
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [openForgetPassword, setOpenForgotPassword] = useState(false);

  // Get consentId from location state if available (when redirected from consent route)
  const consentIdFromState = location.state?.consentId || consentId;

  // USEFORM HOOK
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  const {
    auth,
    setAuth,
    persist,
    setPersist,
    redirectUri,
    currentTenant,
    getTenantIdFromUrl,
    setAuthWithTenant,
  } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const { tenantLogo, hideDefaultBranding } = useTenantBranding();

  const { mutate, isLoading } = useMutation((auth) => {
    return axios.post("/login", auth, { withCredentials: true });
  });

  const { data: identityProviders } = useQuery(
    ["registered_identity_providers"],
    () => {
      const tenantId = currentTenant?.id || getTenantIdFromUrl();
      return axios.get(`/registeredIdentityProviders`, {
        params: {
          page: -1,
          ...(tenantId && { tenant_id: tenantId }),
        },
      });
    },
    {
      refetchOnWindowsFocus: false,
      enable: false,
    }
  );

  useEffect(() => {
    localStorage.setItem("persist", persist);
  }, [persist]);

  // HELPERS
  const onSubmit = async (formData) => {
    setPersist(true);

    // Include tenant_id in login credentials if available
    const tenantId = currentTenant?.id || getTenantIdFromUrl();
    const loginData = {
      ...formData,
      ...(tenantId && { tenant_id: tenantId }),
    };

    return mutate(loginData, {
      onSuccess: (data) => {
        const authData = data?.data?.data;

        if (tenantId) setAuthWithTenant(authData, tenantId);
        else setAuth(authData);

        queryClient.clear();
        // The component will re-render with auth token and show consent
      },
      onError: (error) => {
        enqueueSnackbar(
          error?.response?.data?.error?.message ||
          error?.message ||
          "Network Error!",
          {
            variant: "error",
            TransitionComponent: Slide,
            preventDuplicate: true,
            autoHideDuration: 2000,
          }
        );
      },
    });
  };

  // RENDER
  if (openForgetPassword)
    return (
      <ForgetPassword open={openForgetPassword} setOpen={setOpenForgotPassword} />
    );

  if (auth?.access_token) {
    if (shouldShowConsentAfterAuth(redirectUri)) {
      return <Consent consentId={consentIdFromState} />;
    }
    const postAuthDest = resolvePostAuthDestination({
      redirectUri,
      from: location.state?.from,
    });
    if (postAuthDest) {
      return <Navigate to={postAuthDest} replace />;
    }
    return <Navigate to="/" replace />;
  }

  if (open) {
    return <RegisterWithEmail setOpenRegister={setOpen} />;
  }

  return (
    <AuthLayout
      title={hideDefaultBranding ? "Sign In" : "Sign In with SSO"}
      onSubmit={handleSubmit(onSubmit)}
      header={
        (tenantLogo || !hideDefaultBranding) && (
          <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
            <Avatar
              sx={{ width: "140px", height: "100px", borderRadius: 1 }}
              src={tenantLogo || logo}
            />
          </Box>
        )
      }
    >
      <Controller
        name="email"
        control={control}
        rules={{
          required: "Email is required",
          pattern: {
            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            message: "Invalid email address",
          },
        }}
        render={({ field }) => (
          <AuthTextField
            variant="email"
            label="Email"
            autoFocus
            error={errors?.email}
            helperText={errors?.email ? errors?.email?.message : ""}
            {...field}
          />
        )}
      />
      <Controller
        name="password"
        control={control}
        rules={{ required: "Password is required" }}
        render={({ field }) => (
          <AuthTextField
            variant="password"
            label="Password"
            error={errors?.password}
            helperText={errors?.password ? errors?.password?.message : ""}
            {...field}
          />
        )}
      />
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          onClick={() => setOpenForgotPassword(true)}
          sx={{
            p: 0.5,
            minWidth: "auto",
            textTransform: "none",
            fontSize: 14,
            color: "text.secondary",
            "&:hover": { bgcolor: "transparent", color: "primary.main" },
          }}
        >
          Forgot password?
        </Button>
      </Box>
      <PrimaryButton
        type="submit"
        loading={isLoading}
        sx={{
          mb: 1.5,
          bgcolor: "primary.main",
          color: "primary.contrastText",
          boxShadow: (t) =>
            `0 3px 5px 2px ${alpha(t.palette.primary.main, 0.3)}`,
          "&:hover": { bgcolor: "primary.dark" },
        }}
      >
        Sign In
      </PrimaryButton>
      <Box
        sx={{
          display: "flex",
          flexDirection: { sm: "row", xs: "column" },
          justifyContent: "center",
          alignItems: "center",
          gap: { xs: 0, sm: 0.5 },
          mb: 1,
        }}
      >
        <Typography
          sx={{
            textAlign: "center",
            fontWeight: 500,
            fontSize: { xs: 14, sm: 15 },
            color: "text.secondary",
          }}
        >
          Don't have an account?
        </Typography>
        <Button
          onClick={() => setOpen(true)}
          sx={{
            color: "primary.main",
            fontWeight: 600,
            textTransform: "none",
            fontSize: { xs: 14, sm: 15 },
            p: { xs: 0.5, sm: 1 },
            minWidth: "auto",
          }}
        >
          Create Account
        </Button>
      </Box>
      {identityProviders?.data?.data?.length > 1 && (
        <>
          <Divider sx={{ my: 2, color: "text.disabled", fontSize: 14 }}>Or</Divider>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {identityProviders?.data?.data?.map((IP) => (
              <Button
                key={IP?.id}
                fullWidth
                variant="outlined"
                sx={{
                  textTransform: "none",
                  borderColor: "divider",
                  color: "text.primary",
                  borderRadius: 2,
                  py: 1,
                  justifyContent: "center",
                  "&:hover": { bgcolor: "action.hover", borderColor: "divider" },
                }}
              >
                <Avatar sx={{ width: 26, height: 26, mr: 1 }} src={IP.logo_uri} />
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  Sign In With {IP.name}
                </Typography>
              </Button>
            ))}
          </Box>
        </>
      )}
    </AuthLayout>
  );
}
export default SignInWithEmail;
